import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Grid,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  InputAdornment,
  Pagination,
  CircularProgress,
  Container,
} from "@mui/material";
import {
  Add,
  Search,
  MoreVert,
  Edit,
  Delete,
  Visibility,
  LocalPharmacy,
} from "@mui/icons-material";
import { medicinesAPI } from "../../services/API";
import { useAuth } from "../../contexts/AuthContext";
import { useSnackbar } from "notistack";

export const Medicines = () => {
  const [medicines, setMedicines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedMedicine, setSelectedMedicine] = useState(null);

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 9,
    total: 0,
    pages: 0,
  });

  const navigate = useNavigate();
  const { hasAnyRole } = useAuth();
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchMedicines();
    }, 500);
    return () => clearTimeout(timer);
  }, [pagination.page, pagination.limit, searchTerm]);

  const fetchMedicines = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...(searchTerm && { search: searchTerm }),
      };

      const response = await medicinesAPI.getAll(params);
      const { medicines: medicinesData, pagination: paginationData } =
        response.data.data;

      setMedicines(medicinesData);
      setPagination((prev) => ({
        ...prev,
        total: paginationData.total,
        pages: paginationData.pages,
      }));
    } catch (error) {
      enqueueSnackbar("Error fetching medicines", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value) => {
    setSearchTerm(value);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (event, value) => {
    setPagination((prev) => ({ ...prev, page: value }));
  };

  const handleMenuOpen = (event, medicine) => {
    setAnchorEl(event.currentTarget);
    setSelectedMedicine(medicine);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedMedicine(null);
  };

  const handleView = () => {
    if (selectedMedicine) navigate(`/hospital/pharmacy/medicines/${selectedMedicine._id}`);
    handleMenuClose();
  };

  const handleEdit = () => {
    if (selectedMedicine) navigate(`/hospital/pharmacy/medicines/${selectedMedicine._id}/edit`);
    handleMenuClose();
  };

  const handleDelete = async () => {
    if (!selectedMedicine) return;

    try {
      await medicinesAPI.delete(selectedMedicine._id);
      enqueueSnackbar("Medicine deleted successfully", { variant: "success" });
      fetchMedicines();
    } catch (error) {
      enqueueSnackbar("Error deleting medicine", { variant: "error" });
    }
    handleMenuClose();
  };

  const getScheduleColor = (schedule) => {
    const colors = {
      non_scheduled: "success",
      schedule_h: "warning",
      schedule_h1: "warning",
      schedule_x: "error",
      narcotic: "error",
    };
    return colors[schedule] || "default";
  };

  const getFormIcon = (form) => {
    const icons = {
      tablet: "💊",
      capsule: "🔴",
      syrup: "🥤",
      injection: "💉",
      ointment: "🧴",
      cream: "🧴",
      drops: "💧",
      inhaler: "🌬️",
      other: "💊",
    };
    return icons[form] || "💊";
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(180deg, #f9fafc 0%, #f2f5f8 100%)",
      }}
    >
      <Container maxWidth="xl" sx={{ py: 5 }}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 4,
          }}
        >
          <Typography variant="h3" fontWeight="bold">
            Medicines
          </Typography>
          {hasAnyRole(["admin", "inventory_manager"]) && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => navigate("/hospital/pharmacy/medicines/new")}
              sx={{
                borderRadius: 2,
                boxShadow: 2,
                transition: "0.3s",
                "&:hover": {
                  boxShadow: 4,
                  transform: "scale(1.05)",
                },
              }}
            >
              Add Medicine
            </Button>
          )}
        </Box>

        {/* Search Bar */}
        <Card
          sx={{
            mb: 4,
            borderRadius: 3,
            boxShadow: 3,
            transition: "0.3s",
            "&:hover": { boxShadow: 5 },
          }}
        >
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Search medicines by name, generic name, or brand..."
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography
                  variant="h6"
                  color="text.secondary"
                  align="center"
                >
                  Total: {pagination.total} medicines
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Medicines Grid */}
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Grid container spacing={3}>
              {medicines.map((medicine) => (
                <Grid item xs={12} sm={6} md={4} key={medicine._id}>
                  <Card
                    sx={{
                      height: "100%",
                      borderRadius: 3,
                      boxShadow: 3,
                      transition: "0.3s",
                      "&:hover": {
                        transform: "translateY(-5px)",
                        boxShadow: 6,
                      },
                    }}
                  >
                    <CardContent>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                          mb: 2,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <Typography variant="h3">
                            {getFormIcon(medicine.form)}
                          </Typography>
                          <Box>
                            <Typography
                              variant="h5"
                              fontWeight="bold"
                              noWrap
                              sx={{ maxWidth: 180 }}
                            >
                              {medicine.name}
                            </Typography>
                            <Typography
                              variant="h6"
                              color="text.secondary"
                            >
                              {medicine.genericName}
                            </Typography>
                          </Box>
                        </Box>
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, medicine)}
                        >
                          <MoreVert />
                        </IconButton>
                      </Box>

                      <Typography
                        variant="h6"
                        color="text.secondary"
                        sx={{ mb: 2 }}
                      >
                        {medicine.brand} • {medicine.strength}
                      </Typography>

                      <Box
                        sx={{
                          display: "flex",
                          gap: 1,
                          flexWrap: "wrap",
                          mb: 2,
                        }}
                      >
                        <Chip
                          label={medicine.form}
                          size="small"
                          variant="outlined"
                          sx={{
                            borderRadius: 2,
                            fontWeight: 500,
                            textTransform: "capitalize",
                          }}
                        />
                        <Chip
                          label={medicine.schedule
                            .replace("_", " ")
                            .toUpperCase()}
                          size="small"
                          color={getScheduleColor(medicine.schedule)}
                          sx={{ borderRadius: 2, fontWeight: 500 }}
                        />
                        <Chip
                          label={medicine.category}
                          size="small"
                          variant="outlined"
                          sx={{ borderRadius: 2 }}
                        />
                      </Box>

                      <Typography
                        variant="h6"
                        color="text.secondary"
                        sx={{ mb: 2, lineHeight: 1.6 }}
                      >
                        {medicine.description && medicine.description.length > 100
                          ? `${medicine.description.substring(0, 100)}...`
                          : medicine.description}
                      </Typography>

                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Typography variant="h6" color="text.secondary">
                          By {medicine.manufacturer}
                        </Typography>
                        <Chip
                          label={medicine.isActive ? "Active" : "Inactive"}
                          size="small"
                          color={medicine.isActive ? "success" : "default"}
                          sx={{
                            borderRadius: 2,
                            fontWeight: 500,
                            px: 1,
                          }}
                        />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {/* No Results */}
            {medicines.length === 0 && !loading && (
              <Card
                sx={{
                  textAlign: "center",
                  py: 6,
                  borderRadius: 3,
                  boxShadow: 2,
                  mt: 4,
                }}
              >
                <CardContent>
                  <LocalPharmacy
                    sx={{
                      fontSize: 64,
                      color: "text.secondary",
                      mb: 2,
                    }}
                  />
                  <Typography
                    variant="h5"
                    color="text.secondary"
                    gutterBottom
                  >
                    No medicines found
                  </Typography>
                  <Typography
                    variant="h6"
                    color="text.secondary"
                    sx={{ mb: 3 }}
                  >
                    {searchTerm
                      ? "Try adjusting your search terms"
                      : "Get started by adding your first medicine"}
                  </Typography>
                  {hasAnyRole(["admin", "inventory_manager"]) && (
                    <Button
                      variant="contained"
                      startIcon={<Add />}
                      onClick={() => navigate("/hospital/pharmacy/medicines/new")}
                      sx={{
                        borderRadius: 2,
                        transition: "0.3s",
                        "&:hover": { transform: "scale(1.05)" },
                      }}
                    >
                      Add First Medicine
                    </Button>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Pagination */}
            {pagination.pages > 1 && (
              <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
                <Pagination
                  count={pagination.pages}
                  page={pagination.page}
                  onChange={handlePageChange}
                  color="primary"
                  showFirstButton
                  showLastButton
                />
              </Box>
            )}
          </>
        )}

        {/* Action Menu */}
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
          <MenuItem onClick={handleView}>
            <Visibility sx={{ mr: 1 }} /> View Details
          </MenuItem>
          {hasAnyRole(["admin", "inventory_manager"]) && (
            <MenuItem onClick={handleEdit}>
              <Edit sx={{ mr: 1 }} /> Edit
            </MenuItem>
          )}
          {hasAnyRole(["admin"]) && (
            <MenuItem onClick={handleDelete}>
              <Delete sx={{ mr: 1 }} /> Delete
            </MenuItem>
          )}
        </Menu>
      </Container>
    </Box>
  );
};